/**
 * Hand-rolled PKCE flow with copy-paste handoff between WKWebView and the
 * user's real browser.
 *
 * Why not @osdk/oauth's createPublicOauthClient? Because it assumes a
 * single-window OAuth flow: window.location.assign() to authorize, store
 * codeVerifier in sessionStorage, expect redirect back to the same window
 * to read window.location.href and complete the exchange.
 *
 * In Excel's WKWebView the multipass authorize page never rendered at
 * all — blank screen, no login UI. Most likely cause is multipass'
 * Content-Security-Policy / frame-ancestors refusing to load inside a
 * non-browser embedded context (the same family of policies that block
 * embedding in iframes). Without a visible login page there is no path
 * to MFA either way. Verified on 2026-05-20 by attempting the switch
 * (see git history: `git log --grep "createPublicOauthClient"`) and
 * reverting.
 *
 * This module bypasses that by handing off auth to the user's real system
 * browser via Office.context.ui.openBrowserWindow, then collecting the
 * callback URL back via a paste form. Don't "simplify" by switching to
 * createPublicOauthClient without first re-testing in Excel — the failure
 * is silent (blank pane) and easy to miss in code review.
 */

/* global Office, fetch, process, URL, URLSearchParams, AbortController, Response, setTimeout, clearTimeout, self */
import { setTokens, type StoredTokens } from "./tokenStore";
import { generateCodeChallenge, generateCodeVerifier, generateState } from "./pkce";

const SCOPES = [
  "api:use-ontologies-read",
  "api:use-ontologies-write",
  "api:use-mediasets-read",
  "api:use-mediasets-write",
  "api:use-sql-queries-read",
  "api:use-sql-queries-execute",
  "api:use-ontology-mcp-read",
];

export type ProgressFn = (msg: string) => void;
export type RequestUrlFn = () => Promise<string>;

export async function signIn(
  requestUrl: RequestUrlFn,
  onProgress: ProgressFn = () => {}
): Promise<StoredTokens> {
  const clientId = process.env.OSDK_CLIENT_ID!;
  const osdkUrl = process.env.OSDK_URL!;
  const redirectUrl = process.env.OSDK_REDIRECT_URL!;

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();
  onProgress("Opening browser for sign-in…");

  const authorizeUrl = new URL(`${osdkUrl}/multipass/api/oauth2/authorize`);
  authorizeUrl.searchParams.set("client_id", clientId);
  authorizeUrl.searchParams.set("response_type", "code");
  authorizeUrl.searchParams.set("redirect_uri", redirectUrl);
  authorizeUrl.searchParams.set("state", state);
  authorizeUrl.searchParams.set("code_challenge", codeChallenge);
  authorizeUrl.searchParams.set("code_challenge_method", "S256");
  authorizeUrl.searchParams.set("scope", SCOPES.join(" "));

  Office.context.ui.openBrowserWindow(authorizeUrl.toString());

  onProgress("Waiting for you to paste the callback URL…");
  const pastedUrl = await requestUrl();

  const { code, returnedState } = parseCallbackUrl(pastedUrl);
  if (returnedState !== state) {
    throw new Error("State mismatch — please try signing in again");
  }

  onProgress("Exchanging code for token…");
  const tokens = await exchangeCodeForTokens(code, codeVerifier, redirectUrl, clientId, osdkUrl);
  await setTokens(tokens);
  onProgress("Signed in. Fetching data…");
  return tokens;
}

function parseCallbackUrl(pastedUrl: string): { code: string; returnedState: string } {
  let parsed: URL;
  try {
    parsed = new URL(pastedUrl.trim());
  } catch {
    throw new Error("That doesn't look like a URL");
  }
  const code = parsed.searchParams.get("code");
  const returnedState = parsed.searchParams.get("state");
  const error = parsed.searchParams.get("error");
  if (error) {
    const desc = parsed.searchParams.get("error_description");
    throw new Error(`Sign-in error: ${error}${desc ? " — " + desc : ""}`);
  }
  if (!code || !returnedState) {
    throw new Error(
      "URL is missing `code` or `state` — make sure you copied the full URL after signing in"
    );
  }
  return { code, returnedState };
}

async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUrl: string,
  clientId: string,
  osdkUrl: string
): Promise<StoredTokens> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUrl,
    client_id: clientId,
    code_verifier: codeVerifier,
  });

  const url = `${osdkUrl}/multipass/api/oauth2/token`;
  // 30s timeout so a hung fetch doesn't leave us spinning forever
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30_000);

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(
        `Token exchange timed out after 30s. URL: ${url}. ` +
          "Most likely cause: CORS preflight blocked. Check that " +
          `${self.location.origin} is in the Foundry CORS allowlist for the swirl host.`
      );
    }
    throw new Error(
      `Token exchange network error: ${err instanceof Error ? err.message : String(err)} ` +
        `(URL: ${url})`
    );
  }
  clearTimeout(timeoutId);

  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  }

  const json = await res.json();
  return {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + Number(json.expires_in ?? 3600) * 1000,
  };
}
